import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import Logo from "../../assets/RCS-Logo-White.png";
import {
  getLiquidationSettlementAmount,
} from "../../utils/liquidationSettlement";
import { API_BASE_URL, authFetch } from "../../config/api";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getUnliquidatedAmount = (report = {}) =>
  (report.actual_cost_rows || []).reduce((sum, row) => {
    if (!row?.is_unliquidated) return sum;
    return sum + Number(row.amount || 0);
  }, 0);

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const getRequestTypeKey = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "non_operational" || normalized === "non-operational") {
    return "non_operational";
  }
  if (normalized === "operational") {
    return "operational";
  }
  if (normalized === "sales") {
    return "sales";
  }
  return "";
};

const isTruthyFlag = (value) =>
  value === true || value === 1 || ["true", "1", "yes", "on"].includes(String(value || "").trim().toLowerCase());

const matchesRequestTypeFilter = (row, typeFilter) => {
  if (typeFilter === "all") return true;
  if (typeFilter === "sales") {
    return isTruthyFlag(row?.for_sales) || getRequestTypeKey(row?.type) === "sales";
  }
  return getRequestTypeKey(row?.type) === typeFilter;
};

const formatRequestTypeLabel = (value) => {
  const key = getRequestTypeKey(value);
  if (key === "non_operational") return "Non-Operational";
  if (key === "operational") return "Operational";
  if (key === "sales") return "Sales";
  return "—";
};

const getJobOrderDisplay = (row) => {
  if (row?.job_order) return row.job_order;
  return getRequestTypeKey(row?.type) === "non_operational" ? "No JO (Non-Operational)" : "—";
};

const readJsonResponse = async (res) => {
  const raw = await res.text();
  try {
    return JSON.parse(raw);
  } catch {
    const preview = raw.trim().slice(0, 120);
    throw new Error(
      preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
        ? "API returned HTML instead of JSON. Check the backend URL or auth session."
        : "API returned an unexpected non-JSON response."
    );
  }
};

const stageMeta = {
  pending_submission: {
    label: "Awaiting docs",
    tone: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    description: "Upload receipts and proof of payment.",
  },
  submitted: {
    label: "Submitted",
    tone: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
    description: "Documents are ready for team leader approval.",
  },
  approved: {
    label: "Approved",
    tone: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    description: "Liquidation is approved by the team leader and ready for journal creation.",
  },
  rejected: {
    label: "Rejected",
    tone: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
    description: "Liquidation was rejected and needs correction.",
  },
  journal_created: {
    label: "Journal created",
    tone: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
    description: "Liquidation is complete and posted to journal.",
  },
  waiting_request_approval: {
    label: "Waiting approval",
    tone: "bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200",
    description: "The request must be approved first.",
  },
};

const stageOrder = [
  "pending_submission",
  "submitted",
  "approved",
  "journal_created",
];

const modalSteps = [
  { key: "submit", title: "Submit", icon: "fa-file-arrow-up" },
  { key: "verify", title: "Team Leader Approval", icon: "fa-circle-check" },
  { key: "journal", title: "Journal", icon: "fa-book" },
];

const actionTimeline = (liquidation) => [
  { label: "Submitted", value: liquidation?.submitted_at, actor: liquidation?.submitted_by_name },
  { label: "Team Leader Approved", value: liquidation?.verified_at, actor: liquidation?.verified_by_name },
  { label: "Approved", value: liquidation?.approved_at, actor: liquidation?.approved_by_name },
  { label: "Rejected", value: liquidation?.rejected_at, actor: liquidation?.rejected_by_name },
  { label: "Journaled", value: liquidation?.journal_created_at, actor: liquidation?.journal_created_by_name },
].filter((item) => item.value || item.actor);

const createActualCostRow = (row = {}) => ({
  id: row.id || `${Date.now()}-${Math.random()}`,
  particulars: row.particulars || "",
  or_no: row.or_no || "",
  amount: row.amount ?? "",
  is_unliquidated:
    row.is_unliquidated === true ||
    row.is_unliquidated === 1 ||
    row.is_unliquidated === "1" ||
    row.is_unliquidated === "true" ||
    row.is_unliquidated === "yes" ||
    row.is_unliquidated === "on" ||
    row.isUnliquidated === true,
});

const createAdvanceRow = (particular = {}) => ({
  id: particular.id || particular.request_particular_id || particular.description || `${Date.now()}-${Math.random()}`,
  request_particular_id: particular.request_particular_id || particular.id || null,
  particulars: particular.particulars || particular.description || particular.name || "",
  or_no: particular.or_no || "",
  amount: particular.total ?? particular.amount ?? 0,
});

const createUploadDocumentRow = () => ({
  id: `${Date.now()}-${Math.random()}`,
  file: null,
  docType: "receipt",
});

const MANUAL_EXPENSE_LABELS = [
  "Freight",
  "Arrastre",
  "Wharfage",
  "Storage",
  "Demurrage",
  "Trucking",
  "Gas & Oil",
  "Toll fee",
  "Parking fee",
  "Others",
];

const LiquidationWorkflow = ({
  portalLabel = "Accounting",
  pageTitle = "Liquidation Queue",
  pageDescription = "Review RFPs marked for liquidation and open the modal to upload documents or complete the workflow.",
  scope = "all",
  hideReviewActions = false,
  hideReportEditor = false,
} = {}) => {
  const user = useSelector((state) => state.auth.user);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportRowsPerPage, setReportRowsPerPage] = useState(10);
  const [activeId, setActiveId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createLiquidationOpen, setCreateLiquidationOpen] = useState(false);
  const [createLiquidationSearch, setCreateLiquidationSearch] = useState("");
  const [manualLiquidationRfp, setManualLiquidationRfp] = useState(null);
  const [manualLiquidationForm, setManualLiquidationForm] = useState({
    reference: "",
    name: "",
    department: "",
    liquidationDate: new Date().toISOString().slice(0, 10),
    requestedDate: "",
    jobOrder: "",
    vehiclePlate: "",
    remarks: "",
    cashAdvance: "",
    expenses: MANUAL_EXPENSE_LABELS.map((label) => ({ label, amount: "" })),
  });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [uploadDocuments, setUploadDocuments] = useState([createUploadDocumentRow()]);
  const [uploadNote, setUploadNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [successModal, setSuccessModal] = useState(null);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [reportDateCreated, setReportDateCreated] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportDock, setReportDock] = useState("");
  const [reportPoNo, setReportPoNo] = useState("");
  const [reportReferenceNo, setReportReferenceNo] = useState("");
  const [reportDeliveryDate, setReportDeliveryDate] = useState("");
  const [actualCostRows, setActualCostRows] = useState([createActualCostRow()]);
  const [advanceRows, setAdvanceRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [downloadLoadingId, setDownloadLoadingId] = useState("");
  const [particularsOptions, setParticularsOptions] = useState([]);
  const [teamLeaderUsers, setTeamLeaderUsers] = useState([]);
  const [selectedTeamLeaderId, setSelectedTeamLeaderId] = useState("");
  const [focusReportSection, setFocusReportSection] = useState(false);
  const reportSectionRef = useRef(null);
  const currentUserId = Number(user?.user_id || user?.id || 0);
  const requestorRows = useMemo(() => {
    if (scope !== "created") return [];
    return requests.filter((row) => {
      const preparerId = row.preparer_user_id || row.preparer_id;
      return String(preparerId || "") === String(currentUserId || "");
    });
  }, [currentUserId, requests, scope]);
  const scopedRequests = scope === "created" ? requestorRows : requests;

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/liquidations`);
      if (scope === "created") {
        url.searchParams.set("scope", "created");
      }
      const res = await authFetch(url, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Failed to load liquidation requests");
      setRequests((json.data || []).filter((row) => row?.for_liquidation === true));
    } catch (err) {
      setError(err.message || "Failed to load liquidation requests");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const loadParticularsOptions = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/particulars`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (json.success) {
        const activeParticulars = (json.data || []).filter((p) => p.status === "active");
        setParticularsOptions(activeParticulars);
      }
    } catch (err) {
      console.error("Failed to load particulars options", err);
    }
  }, []);

  const loadTeamLeaderUsers = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/liquidations/team-leader-users`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (json.success) {
        setTeamLeaderUsers(json.data || []);
      }
    } catch (err) {
      console.error("Failed to load team leader users", err);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    loadParticularsOptions();
    if (!hideReportEditor) {
      loadTeamLeaderUsers();
    }
  }, [hideReportEditor, loadParticularsOptions, loadRequests, loadTeamLeaderUsers]);

  useEffect(() => {
    if (!activeId && requests[0]) {
      setActiveId(requests[0].id);
    }
  }, [activeId, requests]);

  useEffect(() => {
    if (!isModalOpen || !activeId) return;

    let cancelled = false;
    const loadDetail = async () => {
      setDetailLoading(true);
      setDetailError("");
      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/liquidations/${activeId}`);
        if (scope === "created") {
          url.searchParams.set("scope", "created");
        }
        const res = await authFetch(url, {
          credentials: "include",
        });
        const json = await readJsonResponse(res);
        if (!json.success) throw new Error(json.message || "Failed to load liquidation details");
        if (!cancelled) {
          setDetail(json.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          setDetail(null);
          setDetailError(err.message || "Failed to load liquidation details");
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [activeId, isModalOpen, scope]);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  const filteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    const liquidationRows = scopedRequests.filter((row) => {
      if (row?.for_liquidation !== true) return false;
      return matchesRequestTypeFilter(row, typeFilter);
    }).filter((row) => {
      if (stageFilter === "all") return true;
      if (stageFilter === "awaiting") return row?.liquidation_stage === "pending_submission";
      if (stageFilter === "in_progress") {
        return String(row?.liquidation_stage || "") === "submitted";
      }
      if (stageFilter === "approved") return row?.liquidation_stage === "approved";
      if (stageFilter === "completed") return row?.liquidation_stage === "journal_created";
      return true;
    });
    if (!term) return liquidationRows;

    return liquidationRows.filter((row) => {
      const haystack = [
        row.rfp_no,
        row.payee,
        row.job_order,
        formatRequestTypeLabel(row.type),
        row.preparer_name,
        row.approver_name,
        row.liquidation_stage,
        row.type
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [scopedRequests, search, typeFilter, stageFilter]);

  const createLiquidationCandidates = useMemo(() => {
    const term = createLiquidationSearch.trim().toLowerCase();
    return scopedRequests
      .filter(
        (row) =>
          row?.for_liquidation === true &&
          !(scope === "created" && String(row?.liquidation_stage || "") === "waiting_request_approval")
      )
      .filter((row) => {
        if (!term) return true;
        return [row.rfp_no, row.payee, row.job_order, formatRequestTypeLabel(row.type)]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .slice(0, 50);
  }, [createLiquidationSearch, scope, scopedRequests]);

  const updateManualLiquidationField = (field, value) => {
    setManualLiquidationForm((current) => ({ ...current, [field]: value }));
  };

  const updateManualExpense = (index, value) => {
    setManualLiquidationForm((current) => ({
      ...current,
      expenses: current.expenses.map((expense, expenseIndex) =>
        expenseIndex === index ? { ...expense, amount: value } : expense
      ),
    }));
  };

  const chooseManualLiquidationRfp = (row) => {
    setManualLiquidationRfp(row);
    setManualLiquidationForm((current) => ({
      ...current,
      reference: row.rfp_no || "",
      name: row.preparer_name || row.payee || "",
      jobOrder: row.job_order || "",
    }));
  };

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

  const liquidationReportRows = useMemo(
    () =>
      scopedRequests.filter((row) => {
        if (row?.for_liquidation !== true || !row.liquidation_report) return false;
        return matchesRequestTypeFilter(row, typeFilter);
      }),
    [scopedRequests, typeFilter]
  );
  const reportTotalPages = Math.max(1, Math.ceil(liquidationReportRows.length / reportRowsPerPage));
  const reportStartIndex = (reportCurrentPage - 1) * reportRowsPerPage;
  const reportEndIndex = reportStartIndex + reportRowsPerPage;
  const paginatedLiquidationReportRows = liquidationReportRows.slice(reportStartIndex, reportEndIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, rowsPerPage, scopedRequests.length, typeFilter, stageFilter]);

  useEffect(() => {
    setReportCurrentPage(1);
  }, [reportRowsPerPage, liquidationReportRows.length, typeFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (reportCurrentPage > reportTotalPages) {
      setReportCurrentPage(reportTotalPages);
    }
  }, [reportCurrentPage, reportTotalPages]);

  const counts = useMemo(() => {
    const liquidationRows = scopedRequests.filter((row) => {
      if (row?.for_liquidation !== true) return false;
      return matchesRequestTypeFilter(row, typeFilter);
    });
    const total = liquidationRows.length;
    const awaiting = liquidationRows.filter((row) => row.liquidation_stage === "pending_submission").length;
    const inProgress = liquidationRows.filter((row) => row.liquidation_stage === "submitted").length;
    const approved = liquidationRows.filter((row) => row.liquidation_stage === "approved").length;
    const completed = liquidationRows.filter((row) => row.liquidation_stage === "journal_created").length;
    return { total, awaiting, inProgress, approved, completed };
  }, [scopedRequests, typeFilter]);

  const activeRequest = useMemo(
    () => scopedRequests.find((row) => row?.for_liquidation === true && String(row.id) === String(activeId)) || null,
    [activeId, scopedRequests]
  );

  const modalData = detail || activeRequest;
  const selectedDocuments = modalData?.liquidation_documents || [];
  const liquidation = modalData?.liquidation || null;
  const savedReport = modalData?.liquidation_report || null;
  const liquidationParticularsOptions = useMemo(() => {
    const options = [
      ...(particularsOptions || []),
      ...(modalData?.particulars || []).map((particular) => ({
        id: `request-${particular.id || particular.description}`,
        name: particular.description || particular.name || "",
      })),
      ...(actualCostRows || []).map((row) => ({
        id: `selected-${row.particulars}`,
        name: row.particulars || "",
      })),
    ].filter((option) => option.name);

    const seen = new Set();
    return options.filter((option) => {
      const key = String(option.name).trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [actualCostRows, modalData?.particulars, particularsOptions]);
  const currentStage = modalData?.liquidation_stage || "pending_submission";
  const isSuperadmin = String(user?.role || "").toLowerCase() === "superadmin";
  const selectedTeamLeaderUserId = Number(
    savedReport?.team_leader_user_id || liquidation?.team_leader_user_id || selectedTeamLeaderId || 0
  );
  const isCurrentUserSelectedTeamLeader =
    selectedTeamLeaderUserId > 0 && selectedTeamLeaderUserId === currentUserId;
  const currentMeta = stageMeta[currentStage] || stageMeta.pending_submission;
  const stageIndex = Math.max(stageOrder.indexOf(currentStage), 0);
  const progressWidth = `${Math.max(0, (stageIndex / (stageOrder.length - 1)) * 100)}%`;
  const isRequestorPortal = scope === "created";
  const isRequestorRowLocked = (row) =>
    isRequestorPortal && String(row?.liquidation_stage || "") === "waiting_request_approval";
  const showSubmissionActions = isRequestorPortal || !hideReviewActions;
  const showAccountingReviewActions = !isRequestorPortal && !hideReviewActions;

  const canUpload =
    showSubmissionActions &&
    (currentStage === "pending_submission" || currentStage === "submitted");
  const canVerify =
    showAccountingReviewActions &&
    currentStage === "submitted" &&
    Boolean(savedReport) &&
    (isSuperadmin || isCurrentUserSelectedTeamLeader);
  const canReject = showAccountingReviewActions && ["submitted", "approved"].includes(currentStage);
  const canCreateReport = !hideReportEditor && ["pending_submission", "submitted", "approved", "journal_created"].includes(currentStage);
  const verifyDisabledReason =
    currentStage === "submitted" && !savedReport
      ? "Save the liquidation report before team leader approval."
      : currentStage === "submitted" && !canVerify
      ? "You are not the assigned team leader, so this approval action is disabled."
      : "";
  const assignedTeamLeader = teamLeaderUsers.find(
    (u) => String(u.user_id) === String(selectedTeamLeaderId || liquidation?.team_leader_user_id || savedReport?.team_leader_user_id || "")
  );
  const teamLeaderDisplayName =
    savedReport?.team_leader_approved_name ||
    savedReport?.team_leader_name ||
    (assignedTeamLeader
      ? [assignedTeamLeader.first_name, assignedTeamLeader.last_name].filter(Boolean).join(" ") ||
        assignedTeamLeader.user_name ||
        "—"
      : "—");
  const approverBadge = savedReport?.approver_signature_url || savedReport?.approved_at
    ? "Approved"
    : "Awaiting approval";

  useEffect(() => {
    const fetchShipment = async () => {
      if (!activeRequest?.job_order) {
        setShipmentDetails(null);
        return;
      }

      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
        url.searchParams.set("q", activeRequest.job_order);
        url.searchParams.set("limit", 1);
        const res = await authFetch(url, { credentials: "include" });
        const json = await readJsonResponse(res);
        if (json.success && json.data?.length) {
          setShipmentDetails(json.data[0]);
        } else {
          setShipmentDetails(null);
        }
      } catch (err) {
        console.error("shipment lookup", err);
        setShipmentDetails(null);
      }
    };

    fetchShipment();
  }, [activeRequest?.job_order]);

  useEffect(() => {
    if (!isModalOpen || !focusReportSection || !reportSectionRef.current) return undefined;

    const timer = window.setTimeout(() => {
      reportSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setFocusReportSection(false);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [activeId, focusReportSection, isModalOpen, savedReport]);

  const refreshList = async (requestId) => {
    await loadRequests();
    if (requestId) {
      setActiveId(String(requestId));
    }
  };

  const resetModalFields = () => {
    setUploadDocuments([createUploadDocumentRow()]);
    setUploadNote("");
    setRejectReason("");
    setDetailError("");
  };

  const clearUploadForm = () => {
    setUploadDocuments([createUploadDocumentRow()]);
    setUploadNote("");
  };

  const showSuccessModal = (title, message) => {
    setSuccessModal({ title, message });
  };

  const addActualCostRow = () => {
    setActualCostRows((rows) => [...rows, createActualCostRow()]);
  };

  const updateActualCostRow = (index, field, value) => {
    setActualCostRows((rows) =>
      rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row))
    );
  };

  const getRequestParticularLabel = (particular) =>
    particular?.description || particular?.name || "";

  const getRequestParticularAmount = (particular) =>
    Number(particular?.total) || Number(particular?.amount) || 0;

  const isRequestParticularAdded = (particular) => {
    const label = getRequestParticularLabel(particular);
    const amount = getRequestParticularAmount(particular);
    return actualCostRows.some(
      (row) => row.particulars === label && Number(row.amount || 0) === amount
    );
  };

  const addRequestParticularToActualCosts = (particular) => {
    if (!particular || isRequestParticularAdded(particular)) return;
    const row = createActualCostRow({
      particulars: getRequestParticularLabel(particular),
      amount: getRequestParticularAmount(particular),
      source_type: "request_particular",
      source_particular_id: particular.id || "",
    });

    setActualCostRows((rows) => {
      const hasOnlyBlankRow =
        rows.length === 1 && !rows[0].particulars && !Number(rows[0].amount || 0);
      return hasOnlyBlankRow ? [row] : [...rows, row];
    });
  };

  const removeRequestParticularFromActualCosts = (particular) => {
    const label = getRequestParticularLabel(particular);
    const amount = getRequestParticularAmount(particular);
    setActualCostRows((rows) => {
      const remaining = rows.filter(
        (row) => !(row.particulars === label && Number(row.amount || 0) === amount)
      );
      return remaining.length ? remaining : [createActualCostRow()];
    });
  };

  const addAllRequestParticularsToActualCosts = (particulars) => {
    const available = (particulars || []).filter(
      (particular) => !isRequestParticularAdded(particular)
    );
    if (!available.length) return;

    setActualCostRows((rows) => {
      const hasOnlyBlankRow =
        rows.length === 1 && !rows[0].particulars && !Number(rows[0].amount || 0);
      const existing = hasOnlyBlankRow ? [] : rows;
      return [
        ...existing,
        ...available.map((particular) =>
          createActualCostRow({
            particulars: getRequestParticularLabel(particular),
            amount: getRequestParticularAmount(particular),
            source_type: "request_particular",
            source_particular_id: particular.id || "",
          })
        ),
      ];
    });
  };

  const removeActualCostRow = (index) => {
    setActualCostRows((rows) => rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const updateAdvanceRow = (key, field, value) => {
    setAdvanceRows((rows) =>
      rows.map((row) => (String(row.id) === String(key) ? { ...row, [field]: value } : row))
    );
  };

  const actualCostTotal = useMemo(
    () =>
      actualCostRows.reduce(
        (sum, row) => sum + (row.is_unliquidated ? 0 : Number(row.amount || 0)),
        0
      ),
    [actualCostRows]
  );

  const advancesTotal = useMemo(
    () =>
      advanceRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [advanceRows]
  );

  const reimbursementTotal = actualCostTotal - advancesTotal;
  const hasUploadFiles = uploadDocuments.some((entry) => Boolean(entry?.file));

  const fetchShipmentSummary = async (jobOrder) => {
    if (!jobOrder) return null;

    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
      url.searchParams.set("q", jobOrder);
      url.searchParams.set("limit", 1);
      const res = await authFetch(url, { credentials: "include" });
      const json = await readJsonResponse(res);
      if (json.success && json.data?.length) {
        return json.data[0];
      }
    } catch (err) {
      console.error("shipment lookup", err);
    }

    return null;
  };

  const buildLiquidationReportHtml = ({
    request,
    reportData,
    shipment,
    reportDateCreatedValue,
    dockValue,
    poNoValue,
    referenceNoValue,
    deliveryDateValue,
    actualRows,
    advanceRowsValue,
    actualCostTotalValue,
    advancesTotalValue,
    reimbursementTotalValue,
  }) => `
    <html>
      <head>
        <title>Liquidation Report ${request?.rfp_no || ""}</title>
        <style>
          *{box-sizing:border-box;}
          body{font-family:"Times New Roman",serif;color:#111;margin:0;background:#fff;}
          .page{max-width:1100px;margin:0 auto;padding:22px 28px 32px;}
          .brand{display:flex;align-items:center;justify-content:center;padding:8px 0 16px;}
          .brand img{height:58px;object-fit:contain;}
          h1{text-align:center;margin:0;font-size:28px;letter-spacing:1px;}
          h2{text-align:center;margin:4px 0 0;font-size:18px;font-weight:700;}
          .meta{margin-top:18px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 24px;font-size:13px;}
          .meta .full{grid-column:1 / -1;}
          .section-title{margin-top:18px;background:#1f3b64;color:#fff;padding:8px 12px;font-size:14px;font-weight:700;letter-spacing:.4px;}
          table{width:100%;border-collapse:collapse;margin-top:10px;font-size:13px;}
          th,td{border:1px solid #111;padding:7px 8px;vertical-align:top;}
          th{background:#f3f5f8;text-align:left;}
          .right{text-align:right;}
          .totals{margin-top:8px;display:flex;justify-content:flex-end;}
          .totals .box{min-width:320px;border:1px solid #111;padding:10px 12px;font-weight:700;}
          .signatures{margin-top:40px;display:grid;grid-template-columns:repeat(2,1fr);gap:24px;}
          .sig-box{text-align:center;padding:12px;}
          .sig-label{font-size:12px;color:#555;margin-bottom:8px;}
          .sig-image{height:60px;display:flex;align-items:center;justify-content:center;margin-bottom:4px;}
          .sig-image img{max-height:60px;max-width:150px;object-fit:contain;}
          .sig-name{font-weight:700;font-size:14px;border-top:1px solid #111;padding-top:8px;min-height:24px;}
          .sig-title{font-size:12px;color:#555;margin-top:2px;}
          .row-flag{display:inline-block;margin-top:4px;padding:2px 8px;border-radius:999px;background:#fff7ed;color:#c2410c;font-size:11px;font-weight:700;letter-spacing:.04em;}
        </style>
      </head>
      <body>
        ${(() => {
          const settlementAmount = getLiquidationSettlementAmount(reimbursementTotalValue);
          const settlementLabel = reimbursementTotalValue < 0
            ? "Return Subtotal"
            : reimbursementTotalValue > 0
              ? "Reimbursement Subtotal"
              : "Liquidation";

          return `
        <div class="page">
          <div class="brand"><img src="${Logo}" alt="RCS Logo"></div>
          <h1>Liquidation Report</h1>
          <h2>BROKERAGE DEPARTMENT LIQUIDATION REPORT</h2>

          <div class="meta">
            <div><strong>Date:</strong> ${reportDateCreatedValue ? new Date(reportDateCreatedValue).toLocaleDateString() : "—"}</div>
            <div><strong>Consignee:</strong> ${shipment?.receiver_name || reportData?.consignee || "—"}</div>
            <div><strong>JO:</strong> ${request?.job_order || reportData?.job_order || "—"}</div>
            <div><strong>Tracking No:</strong> ${shipment?.tracking_no || reportData?.tracking_no || "—"}</div>
            <div class="full"><strong>Shipment Details:</strong> ${shipment?.shipment_description || reportData?.shipment_details || "—"}</div>
            <div><strong>Dock:</strong> ${dockValue || "—"}</div>
            <div><strong>P.O No:</strong> ${poNoValue || "—"}</div>
            <div><strong>Reference No:</strong> ${referenceNoValue || "—"}</div>
            <div><strong>Delivery Date:</strong> ${deliveryDateValue || "—"}</div>
          </div>

          <div class="section-title">COST INCURRED FOR SHIPMENT</div>
          <table>
            <thead>
              <tr>
                <th>Particulars</th>
                <th>OR No.</th>
                <th class="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${actualRows
                .map(
                  (row) => `
                  <tr>
                    <td>${row.particulars || "—"}</td>
                    <td>${row.or_no || "—"}${row.is_unliquidated ? `<div class="row-flag">Unliquidated</div>` : ""}</td>
                    <td class="right">${money(row.amount)}</td>
                  </tr>`
                )
                .join("")}
            </tbody>
          </table>
          <div class="totals"><div class="box">TOTAL ACTUAL COST INCURRED: ₱${money(actualCostTotalValue)}</div></div>

          <div class="section-title">ADVANCES TABLE</div>
          <table>
            <thead>
              <tr>
                <th>Particulars</th>
                <th>OR No.</th>
                <th class="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${advanceRowsValue
                .map(
                  (row) => `
                    <tr>
                      <td>${row.particulars || "—"}</td>
                      <td>${row.or_no || "—"}</td>
                      <td class="right">${money(row.amount)}</td>
                    </tr>`
                )
                .join("")}
            </tbody>
          </table>
          <div class="totals"><div class="box">TOTAL ADVANCES: ₱${money(advancesTotalValue)}</div></div>

          <div class="totals" style="margin-top:14px;">
            <div class="box">${settlementLabel}: ₱${money(settlementAmount)}</div>
          </div>

          <div class="signatures">
            <div class="sig-box">
              <div class="sig-label">Prepared by:</div>
              <div class="sig-image">
                ${reportData?.preparer_signature_url ? `<img src="${reportData.preparer_signature_url}" alt="Preparer Signature" />` : ""}
              </div>
              <div class="sig-name">${reportData?.preparer_name || reportData?.created_by_name || "—"}</div>
              <div class="sig-title">Preparer</div>
            </div>
            <div class="sig-box">
              <div class="sig-label">Noted by:</div>
              <div class="sig-image">
                ${reportData?.team_leader_approved_signature_url || reportData?.team_leader_signature_url ? `<img src="${reportData.team_leader_approved_signature_url || reportData.team_leader_signature_url}" alt="Team Leader Signature" />` : ""}
              </div>
              <div class="sig-name">${reportData?.team_leader_approved_name || reportData?.team_leader_name || "—"}</div>
              <div class="sig-title">Brokerage Team Leader</div>
            </div>
          </div>
        </div>
      `;
        })()}
      </body>
    </html>
  `;

  useEffect(() => {
    if (!activeRequest?.id) return;

    // The list endpoint intentionally uses lightweight rows and does not
    // include nested particulars. Once the modal detail is loaded, use its
    // request particulars for the advances table.
    const requestParticulars = modalData?.particulars || activeRequest?.particulars || [];

    // Build advances from request particulars, merging OR numbers from saved report if available
    const buildAdvanceRows = () => {
      const savedAdvanceMap = new Map();
      if (savedReport?.advance_rows?.length) {
        savedReport.advance_rows.forEach((row) => {
          if (row.request_particular_id) {
            savedAdvanceMap.set(row.request_particular_id, row);
          }
        });
      }

      const rows = requestParticulars.map((particular) => {
        const savedRow = savedAdvanceMap.get(particular.id);
        // Always use request's total amount, only merge OR number from saved report
        const requestAmount = Number(particular.total) || Number(particular.amount) || 0;
        return {
          id: particular.id || `${Date.now()}-${Math.random()}`,
          request_particular_id: particular.id || null,
          particulars: particular.description || particular.name || "",
          or_no: savedRow?.or_no || "",
          amount: requestAmount,
        };
      });

      // Older/non-operational RFPs may have a total amount but no linked
      // accounting_request_particulars row. Keep the advance section useful
      // without changing the stored request or standard workflow.
      if (!rows.length && !savedReport?.advance_rows?.length && getRequestTypeKey(activeRequest?.type) === "non_operational") {
        const requestTotal = Number(activeRequest?.total_amount || 0);
        if (requestTotal > 0) {
          return [{
            id: `request-total-${activeRequest.id}`,
            request_particular_id: null,
            particulars: "Request Amount",
            or_no: "",
            amount: requestTotal,
          }];
        }
      }

      return rows;
    };

    if (savedReport) {
      setReportDateCreated(savedReport.report_date_created || new Date().toISOString().slice(0, 10));
      setReportDock(savedReport.dock || "");
      setReportPoNo(savedReport.po_no || "");
      setReportReferenceNo(savedReport.reference_no || "");
      setReportDeliveryDate(savedReport.delivery_date || "");
      setSelectedTeamLeaderId(
        savedReport.team_leader_user_id || savedReport.team_leader_approved_user_id
          ? String(savedReport.team_leader_user_id || savedReport.team_leader_approved_user_id)
          : liquidation?.team_leader_user_id
            ? String(liquidation.team_leader_user_id)
            : ""
      );
      setActualCostRows(
        (savedReport.actual_cost_rows?.length ? savedReport.actual_cost_rows : [createActualCostRow()]).map((row) =>
          createActualCostRow(row)
        )
      );
      setAdvanceRows(buildAdvanceRows());
      return;
    }

    setReportDateCreated(new Date().toISOString().slice(0, 10));
    setReportDock("");
    setReportPoNo("");
    setReportReferenceNo("");
    setReportDeliveryDate("");
    setSelectedTeamLeaderId(liquidation?.team_leader_user_id ? String(liquidation.team_leader_user_id) : "");
    setActualCostRows([createActualCostRow()]);
    setAdvanceRows(requestParticulars.map((particular) => createAdvanceRow(particular)));
  }, [
    activeRequest?.id,
    activeRequest?.particulars,
    activeRequest?.total_amount,
    activeRequest?.type,
    modalData?.particulars,
    savedReport,
    liquidation?.team_leader_user_id,
  ]);

  const buildLiquidationReportPayload = () => {
    const resolvedTeamLeaderId =
      selectedTeamLeaderId || savedReport?.team_leader_user_id || liquidation?.team_leader_user_id || "";
    const selectedTeamLeader = teamLeaderUsers.find((u) => String(u.user_id) === String(resolvedTeamLeaderId));
    return {
      report_date_created: reportDateCreated,
      consignee: shipmentDetails?.receiver_name || savedReport?.consignee || modalData?.consignee || "",
      job_order: modalData?.job_order || savedReport?.job_order || "",
      tracking_no: shipmentDetails?.tracking_no || savedReport?.tracking_no || "",
      shipment_details: shipmentDetails?.shipment_description || savedReport?.shipment_details || "",
      dock: reportDock,
      po_no: reportPoNo,
      reference_no: reportReferenceNo,
      delivery_date: reportDeliveryDate,
      team_leader_user_id: resolvedTeamLeaderId ? Number(resolvedTeamLeaderId) : null,
      team_leader_name: selectedTeamLeader
        ? [selectedTeamLeader.first_name, selectedTeamLeader.last_name].filter(Boolean).join(" ") ||
          selectedTeamLeader.user_name
        : null,
      actual_cost_rows: actualCostRows,
      advance_rows: advanceRows,
    };
  };

  const persistLiquidationReport = async (payload, { includeDocuments = false, formData = null } = {}) => {
    if (!activeRequest) return null;

    const endpoint = includeDocuments
      ? `${API_BASE_URL}/api/accounting/liquidations/${activeRequest.id}/documents`
      : `${API_BASE_URL}/api/accounting/liquidations/${activeRequest.id}/report`;

    const requestOptions = includeDocuments
      ? {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      : {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        };

    const res = await authFetch(endpoint, requestOptions);
    return readJsonResponse(res);
  };

  const saveManualLiquidation = async () => {
    if (!manualLiquidationRfp) return;
    const advanceRowsValue = Number(manualLiquidationForm.cashAdvance || 0) > 0
      ? [{ particulars: "Cash Advance", amount: Number(manualLiquidationForm.cashAdvance), sort_order: 0 }]
      : [];
    if (!advanceRowsValue.length) {
      const message = "An advance line item is required before saving the liquidation.";
      setError(message);
      window.alert(message);
      return;
    }

    const actualRows = manualLiquidationForm.expenses
      .filter((expense) => Number(expense.amount || 0) > 0)
      .map((expense, index) => ({
        particulars: expense.label,
        amount: Number(expense.amount || 0),
        sort_order: index,
      }));
    if (!actualRows.length) {
      const message = "At least one expense (cost incurred) is required before saving the liquidation.";
      setError(message);
      window.alert(message);
      return;
    }

    setReportLoading(true);
    setError("");
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/accounting/liquidations/${manualLiquidationRfp.id}/report`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            report_date_created: manualLiquidationForm.liquidationDate,
            reference_no: manualLiquidationForm.reference,
            consignee: manualLiquidationForm.name,
            job_order: manualLiquidationForm.jobOrder,
            delivery_date: manualLiquidationForm.requestedDate,
            dock: manualLiquidationForm.vehiclePlate,
            shipment_details: [manualLiquidationForm.department, manualLiquidationForm.remarks]
              .filter(Boolean)
              .join(" | "),
            report_format: "manual",
            manual_name: manualLiquidationForm.name,
            manual_department: manualLiquidationForm.department,
            manual_vehicle_plate: manualLiquidationForm.vehiclePlate,
            manual_remarks: manualLiquidationForm.remarks,
            actual_cost_rows: actualRows,
            advance_rows: advanceRowsValue,
          }),
        }
      );
      const json = await readJsonResponse(response);
      if (!json.success) throw new Error(json.message || "Failed to save manual liquidation");
      await loadRequests();
      setManualLiquidationRfp(null);
      setCreateLiquidationOpen(false);
      showSuccessModal("Manual liquidation saved", "The manual liquidation was connected to the selected RFP.");
    } catch (err) {
      setError(err.message || "Failed to save manual liquidation");
    } finally {
      setReportLoading(false);
    }
  };

  const syncSavedReportState = (responseData) => {
    if (!responseData) return;

    const updatedLiquidation = responseData.liquidation || null;
    const updatedReport = responseData.liquidation_report || null;

    if (updatedLiquidation || updatedReport) {
      setDetail((current) =>
        current
          ? {
              ...current,
              liquidation: updatedLiquidation || current.liquidation,
              liquidation_stage: updatedLiquidation?.status || current.liquidation_stage,
              liquidation_report: updatedReport || current.liquidation_report,
            }
          : current
      );
    }
  };

  const submitLiquidationReport = async ({ includeDocuments = false } = {}) => {
    if (!activeRequest) return;

    const validUploads = uploadDocuments.filter((entry) => entry?.file);
    const isReportOnlySave = !includeDocuments;
    const resolvedTeamLeaderId =
      selectedTeamLeaderId || savedReport?.team_leader_user_id || liquidation?.team_leader_user_id || "";

    if (!advanceRows.length) {
      const message = "Add at least one advance line item before saving the liquidation report.";
      setDetailError(message);
      window.alert(message);
      return;
    }

    const missingActualParticular = actualCostRows.some(
      (row) => Number(row?.amount || 0) > 0 && !String(row?.particulars || "").trim()
    );
    if (missingActualParticular) {
      const message = "Select or enter a particular for every actual-cost line with an amount.";
      setDetailError(message);
      window.alert(message);
      return;
    }

    const hasActualCostAmount = actualCostRows.some((row) => Number(row?.amount || 0) > 0);
    if (!hasActualCostAmount) {
      const message = "Add at least one cost-incurred line item with an amount before saving the liquidation report.";
      setDetailError(message);
      window.alert(message);
      return;
    }

    if (includeDocuments && !validUploads.length) {
      setDetailError("Add at least one document before submitting liquidation documents.");
      return;
    }
    if (!selectedTeamLeaderUserId) {
      setDetailError(
        isReportOnlySave
          ? "Select a team leader before saving the liquidation report."
          : "Select a team leader before uploading liquidation documents."
      );
      return;
    }

    setActionLoading(includeDocuments ? "upload" : "save");
    setDetailError("");
    try {
      setReportLoading(true);
      const reportPayload = buildLiquidationReportPayload();
      let json;

      if (includeDocuments) {
        const selectedTeamLeader = teamLeaderUsers.find((u) => String(u.user_id) === String(resolvedTeamLeaderId));
        const selectedTeamLeaderName = selectedTeamLeader
          ? [selectedTeamLeader.first_name, selectedTeamLeader.last_name].filter(Boolean).join(" ") || selectedTeamLeader.user_name
          : "";
        const formData = new FormData();
        validUploads.forEach((entry) => {
          formData.append("files", entry.file);
        });
        formData.append("doc_types", JSON.stringify(validUploads.map((entry) => entry.docType || "receipt")));
        formData.append("report_payload", JSON.stringify(reportPayload));
        formData.append("remarks", uploadNote);
        formData.append("team_leader_user_id", String(resolvedTeamLeaderId));
        formData.append("team_leader_name", selectedTeamLeaderName);

        json = await persistLiquidationReport(reportPayload, { includeDocuments: true, formData });
      } else {
        json = await persistLiquidationReport(reportPayload);
      }

      if (!json.success) throw new Error(json.message || "Failed to save liquidation report");

      syncSavedReportState(json.data || null);
      if (includeDocuments) {
        clearUploadForm();
      }
      showSuccessModal(
        includeDocuments
          ? "Documents submitted and report saved"
          : savedReport
          ? "Report updated"
          : "Report saved",
        includeDocuments
          ? "The liquidation documents were uploaded and the liquidation report was saved successfully."
          : "The liquidation report was updated and saved successfully."
      );
      await refreshList(activeRequest.id);
      setIsModalOpen(true);
    } catch (err) {
      setDetailError(err.message || "Failed to save liquidation report");
    } finally {
      setActionLoading("");
      setReportLoading(false);
    }
  };

  const buildManualLiquidationHtml = ({ request, reportData }) => {
    const expenses = reportData?.actual_cost_rows || [];
    const expenseMap = new Map(expenses.map((row) => [String(row.particulars || "").toLowerCase(), row.amount]));
    const preparerSignatureUrl =
      reportData?.preparer_signature_url ||
      request?.preparer_signature_url ||
      request?.created_by_signature_url ||
      "";
    const teamLeaderSignatureUrl =
      reportData?.team_leader_approved_signature_url ||
      reportData?.team_leader_signature_url ||
      request?.team_leader_approved_signature_url ||
      request?.team_leader_signature_url ||
      "";
    const cashAdvance = (reportData?.advance_rows || []).find(
      (row) => String(row.particulars || "").toLowerCase() === "cash advance"
    )?.amount || reportData?.total_advances || 0;
    const expenseRows = MANUAL_EXPENSE_LABELS.map(
      (label) => `<tr><td>${label}</td><td>${money(expenseMap.get(label.toLowerCase()) || 0)}</td></tr>`
    ).join("");

    return `<html><head><title>Liquidation Form ${request?.rfp_no || ""}</title><style>
    @page{size:A4;margin:18mm 20mm}body{font-family:Arial,sans-serif;color:#111;margin:0;font-size:12px}.page{max-width:760px;margin:0 auto}.brand{text-align:center;margin-bottom:4px}.brand strong{font-size:18px}.title{text-align:center;font-weight:700;margin:4px 0 22px}.meta{display:grid;grid-template-columns:1fr 1fr;gap:10px 28px}.field{border-bottom:1px solid #777;min-height:18px}.full{grid-column:1/-1}.label{font-weight:700}.section{margin-top:24px}.section h3{font-size:13px;margin:0 0 8px}.line{border-bottom:1px solid #777;height:20px}.expenses{width:100%;border-collapse:collapse}.expenses td{padding:2px 0}.expenses td:last-child{text-align:right;border-bottom:1px solid #777;width:42%}.signatures{display:grid;grid-template-columns:1fr 1fr;gap:70px;margin-top:70px}.signature{position:relative;border-top:1px solid #777;padding-top:52px}.signature-image{position:absolute;left:0;top:-46px;width:100%;height:44px;display:flex;align-items:flex-end}.signature-image img{display:block;max-height:44px;max-width:180px;object-fit:contain}.muted{color:#555}
    </style></head><body><div class="page"><div class="brand"><strong>RCS LOGISTICS (PHILS.) INC</strong></div><div class="title">Liquidation Form</div><div class="meta">
      <div>Reference: <span class="field">${reportData?.reference_no || request?.rfp_no || ""}</span></div><div>Liquidation Date: <span class="field">${reportData?.report_date_created || ""}</span></div>
      <div>Name: <span class="field">${reportData?.manual_name || reportData?.consignee || ""}</span></div><div>Requested Date: <span class="field">${reportData?.delivery_date || ""}</span></div>
      <div>Department: <span class="field">${reportData?.manual_department || ""}</span></div><div>Vehicle Plate No.: <span class="field">${reportData?.manual_vehicle_plate || reportData?.dock || ""}</span></div>
      <div>Job Order No.: <span class="field">${reportData?.job_order || request?.job_order || ""}</span></div><div class="full">Remarks: <span class="field">${reportData?.manual_remarks || ""}</span></div>
    </div><div class="section"><div class="line"><span class="label">Cash Advance</span><span style="float:right">${money(cashAdvance)}</span></div></div>
    <div class="section"><h3>Expenses:</h3><table class="expenses">${expenseRows}</table></div><div class="section"><div class="line"><span class="label">Return (refund)</span><span style="float:right">${money(reportData?.return_amount || 0)}</span></div></div>
    <div class="signatures"><div class="signature"><div class="signature-image">${preparerSignatureUrl ? `<img src="${preparerSignatureUrl}" alt="Preparer signature">` : ""}</div>Print name &amp; Signature<br/><span class="muted">${reportData?.manual_name || reportData?.preparer_name || ""}</span></div><div class="signature"><div class="signature-image">${teamLeaderSignatureUrl ? `<img src="${teamLeaderSignatureUrl}" alt="Team leader signature">` : ""}</div>Team Leader<br/><span class="muted">${reportData?.team_leader_approved_name || reportData?.team_leader_name || ""}</span></div></div></div></body></html>`;
  };

  const downloadSavedReport = async (requestRow) => {
    if (!requestRow?.liquidation_report) return;

    setDownloadLoadingId(String(requestRow.id));
    try {
      // The list endpoint intentionally returns a lightweight report summary.
      // Load the full report only when the user requests a download.
      let reportData = requestRow.liquidation_report || {};
      if (
        !reportData.advance_rows?.length ||
        !reportData.actual_cost_rows?.length ||
        !(reportData.team_leader_approved_signature_url || reportData.team_leader_signature_url)
      ) {
        const detailUrl = new URL(`${API_BASE_URL}/api/accounting/liquidations/${requestRow.id}`);
        if (scope === "created") detailUrl.searchParams.set("scope", "created");
        const detailResponse = await authFetch(detailUrl, { credentials: "include" });
        const detailJson = await readJsonResponse(detailResponse);
        if (!detailResponse.ok || !detailJson.success) {
          throw new Error(detailJson.message || "Failed to load the full liquidation report");
        }
        reportData = detailJson.data?.liquidation_report || reportData;
      }
      const shipment = await fetchShipmentSummary(requestRow.job_order);
      const actualRows = reportData.actual_cost_rows?.length ? reportData.actual_cost_rows : [];
      const advanceRowsValue = reportData.advance_rows?.length ? reportData.advance_rows : [];
      const reportHtml = reportData?.report_format === "manual"
        ? buildManualLiquidationHtml({ request: requestRow, reportData })
        : buildLiquidationReportHtml({
            request: requestRow,
            reportData,
            shipment,
            reportDateCreatedValue: reportData.report_date_created || requestRow.created_at,
            dockValue: reportData.dock || "",
            poNoValue: reportData.po_no || "",
            referenceNoValue: reportData.reference_no || "",
            deliveryDateValue: reportData.delivery_date || "",
            actualRows,
            advanceRowsValue,
            actualCostTotalValue: reportData.total_actual_cost_incurred || 0,
            advancesTotalValue: reportData.total_advances || 0,
            reimbursementTotalValue: reportData.total_reimbursement_return || 0,
          });

      const win = window.open("", "_blank");
      if (!win) return;
      win.document.write(reportHtml);
      win.document.close();
      win.focus();

      let printed = false;
      const triggerPrint = () => {
        if (printed) return;
        printed = true;
        try {
          win.print();
        } catch (printErr) {
          console.error("Failed to open print dialog", printErr);
        }
      };

      const images = Array.from(win.document.images || []);
      const pendingImages = images.filter((img) => !img.complete);
      if (!pendingImages.length) {
        window.setTimeout(triggerPrint, 100);
      } else {
        let remaining = pendingImages.length;
        const onImageSettled = () => {
          remaining -= 1;
          if (remaining <= 0) {
            window.setTimeout(triggerPrint, 100);
          }
        };
        pendingImages.forEach((img) => {
          img.addEventListener("load", onImageSettled, { once: true });
          img.addEventListener("error", onImageSettled, { once: true });
        });
        // Safety net in case an image never fires load/error.
        window.setTimeout(triggerPrint, 3000);
      }
    } finally {
      setDownloadLoadingId("");
    }
  };

  const openModal = (requestId, focusReport = false) => {
    setActiveId(String(requestId));
    setFocusReportSection(focusReport);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFocusReportSection(false);
    resetModalFields();
  };

  const addUploadDocumentRow = () => {
    setUploadDocuments((current) => [...current, createUploadDocumentRow()]);
  };

  const removeUploadDocumentRow = (id) => {
    setUploadDocuments((current) => {
      if (current.length <= 1) return current;
      return current.filter((entry) => entry.id !== id);
    });
  };

  const updateUploadDocumentRow = (id, field, value) => {
    setUploadDocuments((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry))
    );
  };

  const handleTransition = async (action, payload = {}) => {
    if (!activeRequest) return;

    setActionLoading(action);
    setDetailError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/liquidations/${activeRequest.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action, ...payload }),
      });
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Failed to update liquidation workflow");

      if (action === "reject") setRejectReason("");

      const successMessages = {
        verify: {
          title: "Liquidation approved",
          message: "The liquidation was approved by the team leader.",
        },
        reject: {
          title: "Liquidation rejected",
          message: "The liquidation was rejected and the reason was saved.",
        },
        journal: {
          title: "Journal created",
          message: "The liquidation journal entry was created successfully.",
        },
      };

      if (successMessages[action]) {
        showSuccessModal(successMessages[action].title, successMessages[action].message);
      }

      await refreshList(activeRequest.id);
      setIsModalOpen(true);
    } catch (err) {
      setDetailError(err.message || "Failed to update liquidation workflow");
    } finally {
      setActionLoading("");
    }
  };

  const timeline = actionTimeline(liquidation);
  const isUploadLoading = actionLoading === "upload";
  const isVerifyLoading = actionLoading === "verify";
  const isRejectLoading = actionLoading === "reject";
  const canSubmitReportAction = hasUploadFiles ? canUpload : canCreateReport;
  const reportActionLabel = hasUploadFiles
    ? "Submit Documents & Save Report"
    : savedReport
    ? "Update Report"
    : "Save Liquidation Report";

  return (
    <div className="space-y-6 font-poppins">
      <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.14em] text-gray-500">{portalLabel}</p>
            <h1 className="text-2xl font-semibold text-blue">{pageTitle}</h1>
            <p className="text-sm text-gray-600">
              {pageDescription}
            </p>
          </div>
          <div className="w-full md:w-80">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search RFP, payee, job order, type"
              className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
            />
          </div>
          {!hideReportEditor ? (
            <button
              type="button"
              onClick={() => {
                setCreateLiquidationSearch("");
                setCreateLiquidationOpen(true);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue/90"
            >
              <i className="fa-solid fa-plus" />
              Create Liquidation
            </button>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {[
            { key: "all", label: "All Types" },
            { key: "operational", label: "Operational" },
            { key: "non_operational", label: "Non-Operational" },
            { key: "sales", label: "Sales" },
          ].map((option) => {
            const active = typeFilter === option.key;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => setTypeFilter(option.key)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  active
                    ? "bg-blue text-white shadow-sm"
                    : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <button
            type="button"
            onClick={() => setStageFilter("all")}
            className={`rounded-2xl border px-4 py-3 text-left transition ${
              stageFilter === "all"
                ? "border-gray-300 bg-white shadow-sm ring-2 ring-gray-200"
                : "border-gray-100 bg-gray-50 hover:bg-gray-100"
            }`}
          >
            <div className="text-xs uppercase tracking-[0.12em] text-gray-500">Total</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{counts.total}</div>
          </button>
          <button
            type="button"
            onClick={() => setStageFilter("awaiting")}
            className={`rounded-2xl border px-4 py-3 text-left transition ${
              stageFilter === "awaiting"
                ? "border-amber-300 bg-amber-100 shadow-sm ring-2 ring-amber-200"
                : "border-amber-100 bg-amber-50 hover:bg-amber-100"
            }`}
          >
            <div className="text-xs uppercase tracking-[0.12em] text-amber-700">Awaiting docs</div>
            <div className="mt-1 text-2xl font-semibold text-amber-800">{counts.awaiting}</div>
          </button>
          <button
            type="button"
            onClick={() => setStageFilter("in_progress")}
            className={`rounded-2xl border px-4 py-3 text-left transition ${
              stageFilter === "in_progress"
                ? "border-sky-300 bg-sky-100 shadow-sm ring-2 ring-sky-200"
                : "border-sky-100 bg-sky-50 hover:bg-sky-100"
            }`}
          >
            <div className="text-xs uppercase tracking-[0.12em] text-sky-700">In progress</div>
            <div className="mt-1 text-2xl font-semibold text-sky-800">{counts.inProgress}</div>
          </button>
          <button
            type="button"
            onClick={() => setStageFilter("approved")}
            className={`rounded-2xl border px-4 py-3 text-left transition ${
              stageFilter === "approved"
                ? "border-indigo-300 bg-indigo-100 shadow-sm ring-2 ring-indigo-200"
                : "border-indigo-100 bg-indigo-50 hover:bg-indigo-100"
            }`}
          >
            <div className="text-xs uppercase tracking-[0.12em] text-indigo-700">Approved</div>
            <div className="mt-1 text-2xl font-semibold text-indigo-800">{counts.approved}</div>
          </button>
          <button
            type="button"
            onClick={() => setStageFilter("completed")}
            className={`rounded-2xl border px-4 py-3 text-left transition ${
              stageFilter === "completed"
                ? "border-emerald-300 bg-emerald-100 shadow-sm ring-2 ring-emerald-200"
                : "border-emerald-100 bg-emerald-50 hover:bg-emerald-100"
            }`}
          >
            <div className="text-xs uppercase tracking-[0.12em] text-emerald-700">Completed</div>
            <div className="mt-1 text-2xl font-semibold text-emerald-800">{counts.completed}</div>
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white/95 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">RFPs for liquidation</h2>
            <p className="text-sm text-gray-600">Click a record to open the liquidation modal.</p>
          </div>
          <div className="text-sm text-gray-500">
            {filteredRequests.length} record{filteredRequests.length === 1 ? "" : "s"}
          </div>
        </div>

        {error ? (
          <div className="m-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-gray-500">
            Loading liquidation records...
          </div>
        ) : filteredRequests.length ? (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                  <th className="px-5 py-4">RFP No</th>
                  <th className="px-5 py-4">Liquidation No.</th>
                  <th className="px-5 py-4">Payee</th>
                  <th className="px-5 py-4">Job Order</th>
                  <th className="px-5 py-4 text-right">Amount</th>
                  <th className="px-5 py-4">Stage</th>
                  <th className="px-5 py-4">Documents</th>
                  <th className="px-5 py-4">Type</th>
                  <th className="px-5 py-4">Format</th>
                  <th className="px-5 py-4">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedRequests.map((row) => {
                  const meta = stageMeta[row.liquidation_stage] || stageMeta.pending_submission;
                  const rowLocked = isRequestorRowLocked(row);
                  return (
                    <tr
                      key={row.id}
                      onClick={rowLocked ? undefined : () => openModal(row.id)}
                      className={`transition ${
                        rowLocked ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:bg-gray-50"
                      }`}
                      aria-disabled={rowLocked ? "true" : "false"}
                    >
                      <td className="px-5 py-4 font-semibold text-blue">{row.rfp_no || "—"}</td>
                      <td className="px-5 py-4 text-sm font-medium text-gray-700">
                        {row.liquidation_no || row.liquidation_report?.liquidation_no || row.liquidation?.liquidation_no || "—"}
                      </td>
                      <td className="px-5 py-4 text-gray-900">{row.payee || "—"}</td>
                      <td className="px-5 py-4 text-gray-600">{getJobOrderDisplay(row)}</td>
                      <td className="px-5 py-4 text-right font-semibold text-gray-900">
                        ₱{money(row.total_amount || 0)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${meta.tone}`}>
                            {meta.label}
                          </span>
                          {rowLocked ? (
                            <span className="inline-flex w-fit rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                              Locked until approved
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        {row.liquidation_documents_count || 0} file{(row.liquidation_documents_count || 0) === 1 ? "" : "s"}
                      </td>
                      <td className="px-5 py-4 text-gray-600">{formatRequestTypeLabel(row.type)}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            row.liquidation_report?.report_format === "manual"
                              ? "bg-violet-100 text-violet-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {row.liquidation_report?.report_format === "manual" ? "Manual" : "Standard"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">
                        {formatDateTime(row.updated_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-5 py-4">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Rows per page:</label>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <span className="text-sm text-gray-600">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredRequests.length)} of {filteredRequests.length} requests
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="First page"
                >
                  <i className="fa-solid fa-angles-left" />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Previous page"
                >
                  <i className="fa-solid fa-angle-left" />
                </button>

                <div className="mx-2 flex items-center gap-1">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          type="button"
                          onClick={() => setCurrentPage(1)}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="start-ellipsis" className="px-1 text-gray-400">
                            ...
                          </span>
                        );
                      }
                    }

                    for (let i = startPage; i <= endPage; i += 1) {
                      pages.push(
                        <button
                          key={i}
                          type="button"
                          onClick={() => setCurrentPage(i)}
                          className={`rounded-lg border px-3 py-1.5 text-sm ${
                            currentPage === i
                              ? "border-blue bg-blue text-white"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="end-ellipsis" className="px-1 text-gray-400">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          type="button"
                          onClick={() => setCurrentPage(totalPages)}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Next page"
                >
                  <i className="fa-solid fa-angle-right" />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Last page"
                >
                  <i className="fa-solid fa-angles-right" />
                </button>
              </div>
            </div>
          </div>
          </>
        ) : (
          <div className="px-5 py-12 text-center text-sm text-gray-500">
            No liquidation records found.
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white/95 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Liquidation reports</h2>
            <p className="text-sm text-gray-600">
              Reports that have already been created. Use view, edit, or download.
            </p>
          </div>
          <div className="text-sm text-gray-500">
            {liquidationReportRows.length} report{liquidationReportRows.length === 1 ? "" : "s"}
          </div>
        </div>

        {liquidationReportRows.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                    <th className="px-5 py-4">RFP No</th>
                    <th className="px-5 py-4">Payee</th>
                    <th className="px-5 py-4">Job Order</th>
                    <th className="px-5 py-4">Type</th>
                    <th className="px-5 py-4">Format</th>
                    <th className="px-5 py-4 text-right">Settlement</th>
                    <th className="px-5 py-4 text-right">Unliquidated</th>
                    <th className="px-5 py-4">Report Date</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {paginatedLiquidationReportRows.map((row) => {
                    const report = row.liquidation_report || {};
                    const settlementValue = Number(row.return_amount ?? (report.total_reimbursement_return || 0));
                    const unliquidatedAmount = getUnliquidatedAmount(report);
                    const rowLocked = isRequestorRowLocked(row);
                    return (
                      <tr
                        key={`report-${row.id}`}
                        className={rowLocked ? "opacity-70" : "hover:bg-gray-50"}
                        aria-disabled={rowLocked ? "true" : "false"}
                      >
                        <td className="px-5 py-4 font-semibold text-blue">{row.rfp_no || "—"}</td>
                        <td className="px-5 py-4 text-gray-900">{row.payee || "—"}</td>
                        <td className="px-5 py-4 text-gray-600">{getJobOrderDisplay(row)}</td>
                        <td className="px-5 py-4 text-gray-600">{formatRequestTypeLabel(row.type)}</td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              report.report_format === "manual"
                                ? "bg-violet-50 text-violet-700 ring-1 ring-violet-200"
                                : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
                            }`}
                          >
                            {report.report_format === "manual" ? "Manual" : "Standard"}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right font-semibold text-gray-900">
                          {settlementValue < 0 ? "Return Subtotal" : "Reimbursement Subtotal"}: ₱{money(Math.abs(settlementValue || 0))}
                        </td>
                        <td className="px-5 py-4 text-right text-sm font-semibold">
                          {unliquidatedAmount > 0 ? (
                            <div className="inline-flex flex-col items-end">
                              <span className="text-amber-700">₱{money(unliquidatedAmount)}</span>
                              <span className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-amber-700 ring-1 ring-amber-200">
                                Unliquidated
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">
                          {report.report_date_created
                            ? new Date(report.report_date_created).toLocaleDateString()
                            : formatDateTime(report.updated_at || row.updated_at)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="inline-flex items-center gap-3">
                            <button
                              type="button"
                              onClick={rowLocked ? undefined : () => openModal(row.id)}
                              disabled={rowLocked}
                              className="text-xs font-semibold text-blue-700 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={rowLocked ? undefined : () => openModal(row.id, true)}
                              disabled={rowLocked}
                              className="text-xs font-semibold text-emerald-700 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={rowLocked ? undefined : () => downloadSavedReport(row)}
                              disabled={rowLocked || downloadLoadingId === String(row.id)}
                              className="text-xs font-semibold text-slate-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {rowLocked
                                ? "Locked"
                                : downloadLoadingId === String(row.id)
                                  ? "Downloading..."
                                  : "Download"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="border-t border-gray-100 px-5 py-4">
              <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Rows per page:</label>
                    <select
                      value={reportRowsPerPage}
                      onChange={(e) => {
                        setReportRowsPerPage(Number(e.target.value));
                        setReportCurrentPage(1);
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <span className="text-sm text-gray-600">
                    Showing {reportStartIndex + 1}-{Math.min(reportEndIndex, liquidationReportRows.length)} of {liquidationReportRows.length} reports
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setReportCurrentPage(1)}
                    disabled={reportCurrentPage === 1}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="First page"
                  >
                    <i className="fa-solid fa-angles-left" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setReportCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={reportCurrentPage === 1}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Previous page"
                  >
                    <i className="fa-solid fa-angle-left" />
                  </button>

                  <div className="mx-2 flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      let startPage = Math.max(1, reportCurrentPage - Math.floor(maxVisiblePages / 2));
                      let endPage = Math.min(reportTotalPages, startPage + maxVisiblePages - 1);

                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }

                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            type="button"
                            onClick={() => setReportCurrentPage(1)}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="report-start-ellipsis" className="px-1 text-gray-400">
                              ...
                            </span>
                          );
                        }
                      }

                      for (let i = startPage; i <= endPage; i += 1) {
                        pages.push(
                          <button
                            key={i}
                            type="button"
                            onClick={() => setReportCurrentPage(i)}
                            className={`rounded-lg border px-3 py-1.5 text-sm ${
                              reportCurrentPage === i
                                ? "border-blue bg-blue text-white"
                                : "border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      if (endPage < reportTotalPages) {
                        if (endPage < reportTotalPages - 1) {
                          pages.push(
                            <span key="report-end-ellipsis" className="px-1 text-gray-400">
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={reportTotalPages}
                            type="button"
                            onClick={() => setReportCurrentPage(reportTotalPages)}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50"
                          >
                            {reportTotalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <button
                    type="button"
                    onClick={() => setReportCurrentPage((prev) => Math.min(prev + 1, reportTotalPages))}
                    disabled={reportCurrentPage === reportTotalPages}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Next page"
                  >
                    <i className="fa-solid fa-angle-right" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setReportCurrentPage(reportTotalPages)}
                    disabled={reportCurrentPage === reportTotalPages}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Last page"
                  >
                    <i className="fa-solid fa-angles-right" />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="px-5 py-12 text-center text-sm text-gray-500">
            No liquidation reports created yet.
          </div>
        )}
      </div>

      {createLiquidationOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[950] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setCreateLiquidationOpen(false)}
            role="presentation"
          />
          <div className="relative z-10 w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Manual liquidation
                </p>
                <h3 className="mt-1 text-2xl font-semibold text-gray-900">
                  {manualLiquidationRfp ? "Manual Liquidation Form" : "Select an RFP"}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Choose the existing RFP that this manually encoded liquidation will be connected to.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCreateLiquidationOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
                aria-label="Close create liquidation dialog"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            {!manualLiquidationRfp ? (
              <>
                <input
                  value={createLiquidationSearch}
                  onChange={(event) => setCreateLiquidationSearch(event.target.value)}
                  placeholder="Search RFP, payee, job order, or type"
                  className="mt-5 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                  autoFocus
                />
                <div className="mt-4 max-h-[min(55vh,28rem)] space-y-2 overflow-y-auto">
                  {createLiquidationCandidates.length ? (
                    createLiquidationCandidates.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => chooseManualLiquidationRfp(row)}
                        className="flex w-full items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left transition hover:border-blue-200 hover:bg-blue-50"
                      >
                        <span className="min-w-0">
                          <span className="block font-semibold text-blue">{row.rfp_no || "RFP"}</span>
                          <span className="mt-1 block truncate text-sm text-gray-700">
                            {row.payee || "—"} • {getJobOrderDisplay(row)}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-semibold text-blue">Encode →</span>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                      No eligible RFPs found.
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-5 max-h-[min(72vh,42rem)] overflow-y-auto pr-1">
                <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl bg-blue-50 px-4 py-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-blue">Connected RFP</div>
                    <div className="mt-1 font-semibold text-gray-900">{manualLiquidationRfp.rfp_no || "RFP"}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setManualLiquidationRfp(null)}
                    className="text-xs font-semibold text-blue hover:underline"
                  >
                    Change RFP
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ["reference", "Reference"],
                    ["name", "Name"],
                    ["department", "Department"],
                    ["liquidationDate", "Liquidation Date", "date"],
                    ["requestedDate", "Requested Date", "date"],
                    ["jobOrder", "Job Order No."],
                    ["vehiclePlate", "Vehicle Plate No."],
                  ].map(([field, label, type = "text"]) => (
                    <label key={field} className="text-sm font-semibold text-gray-700">
                      {label}
                      <input
                        type={type}
                        value={manualLiquidationForm[field]}
                        onChange={(event) => updateManualLiquidationField(field, event.target.value)}
                        className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-300"
                      />
                    </label>
                  ))}
                  <label className="text-sm font-semibold text-gray-700 sm:col-span-2">
                    Remarks
                    <textarea
                      value={manualLiquidationForm.remarks}
                      onChange={(event) => updateManualLiquidationField("remarks", event.target.value)}
                      rows={2}
                      className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-300"
                    />
                  </label>
                </div>

                <div className="mt-5 rounded-2xl border border-gray-200 p-4">
                  <div className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">Expenses</div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Cash Advance
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={manualLiquidationForm.cashAdvance}
                      onChange={(event) => updateManualLiquidationField("cashAdvance", event.target.value)}
                      className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-300"
                    />
                  </label>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {manualLiquidationForm.expenses.map((expense, index) => (
                      <label key={expense.label} className="text-sm text-gray-700">
                        {expense.label}
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expense.amount}
                          onChange={(event) => updateManualExpense(index, event.target.value)}
                          className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-300"
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setCreateLiquidationOpen(false)}
                    className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveManualLiquidation}
                    disabled={reportLoading}
                    className="rounded-xl bg-blue px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue/90 disabled:opacity-50"
                  >
                    {reportLoading ? "Saving..." : "Save Manual Liquidation"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {isModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-6">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={closeModal}
            role="presentation"
          />
          <div className="relative z-10 w-full max-w-[1180px]">
            <div className="max-h-[calc(100vh-1.5rem)] overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-black/5 sm:max-h-[calc(100vh-3rem)]">
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Liquidation modal</p>
                  <h3 className="truncate text-2xl font-semibold text-gray-900">
                    {modalData?.rfp_no || "Liquidation details"}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {modalData?.payee || "—"} | {getJobOrderDisplay(modalData)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                  aria-label="Close modal"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>

              <div className="max-h-[calc(100vh-8rem)] overflow-y-auto lg:max-h-[calc(100vh-9rem)]">
                <div className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
                  <div className="border-b border-gray-100 lg:border-b-0 lg:border-r">
                    <div className="p-6">
                    {detailError ? (
                      <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {detailError}
                      </div>
                    ) : null}

                    {detailLoading && !detail ? (
                      <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-500">
                        Loading liquidation details...
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${currentMeta.tone}`}>
                            {currentMeta.label}
                          </span>
                          <span className="text-sm text-gray-500">
                            {selectedDocuments.length} document{selectedDocuments.length === 1 ? "" : "s"}
                          </span>
                        </div>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-2xl bg-gray-50 px-4 py-3">
                            <div className="text-xs uppercase tracking-[0.12em] text-gray-500">Amount</div>
                            <div className="mt-1 text-lg font-semibold text-gray-900">
                              ₱{money(modalData?.total_amount || 0)}
                            </div>
                          </div>
                          <div className="rounded-2xl bg-gray-50 px-4 py-3">
                            <div className="text-xs uppercase tracking-[0.12em] text-gray-500">For liquidation</div>
                            <div className="mt-1 text-lg font-semibold text-gray-900">
                              {modalData?.for_liquidation ? "Yes" : "No"}
                            </div>
                          </div>
                          <div className="rounded-2xl bg-gray-50 px-4 py-3">
                            <div className="text-xs uppercase tracking-[0.12em] text-gray-500">Type</div>
                            <div className="mt-1 text-lg font-semibold text-gray-900">
                              {formatRequestTypeLabel(modalData?.type)}
                            </div>
                          </div>
                          <div className="rounded-2xl bg-gray-50 px-4 py-3">
                            <div className="text-xs uppercase tracking-[0.12em] text-gray-500">Preparer</div>
                            <div className="mt-1 text-lg font-semibold text-gray-900">
                              {modalData?.preparer_name || "—"}
                            </div>
                          </div>
                          <div className="rounded-2xl bg-gray-50 px-4 py-3">
                            <div className="text-xs uppercase tracking-[0.12em] text-gray-500">Approver</div>
                            <div className="mt-1 text-lg font-semibold text-gray-900">
                              {teamLeaderDisplayName}
                            </div>
                          </div>
                        </div>

                        <div className="mt-5 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                            <i className="fa-solid fa-circle-info text-blue" />
                            Status
                          </div>
                          <p className="mt-2 text-sm text-gray-600">{currentMeta.description}</p>
                          {liquidation?.remarks ? (
                            <p className="mt-2 text-sm text-gray-700">
                              <span className="font-semibold">Notes:</span> {liquidation.remarks}
                            </p>
                          ) : null}
                        </div>

                        <div className="mt-5">
                          <div className="flex items-center justify-between gap-3">
                            <h4 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Workflow progress
                            </h4>
                            <span className="text-xs text-gray-500">{stageIndex + 1} of 5</span>
                          </div>
                          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full bg-blue transition-all"
                              style={{ width: progressWidth }}
                            />
                          </div>
                          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                            {modalSteps.map((step, idx) => {
                              const active = idx <= Math.min(stageIndex, 3);
                              return (
                                <div
                                  key={step.key}
                                  className={`rounded-2xl border px-3 py-3 ${
                                    active ? "border-blue-200 bg-blue-50" : "border-gray-100 bg-white"
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div
                                      className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                                        active ? "bg-blue text-white" : "bg-gray-100 text-gray-500"
                                      }`}
                                    >
                                      <i className={`fa-solid ${step.icon}`} />
                                    </div>
                                    <span className="text-xs uppercase tracking-[0.12em] text-gray-500">
                                      {String(idx + 1).padStart(2, "0")}
                                    </span>
                                  </div>
                                  <div className="mt-3 text-sm font-semibold text-gray-900">{step.title}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div className="mt-5">
                          <div className="flex items-center justify-between gap-3">
                            <h4 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Documents
                            </h4>
                            <span className="text-sm text-gray-500">Uploaded files and hardcopy records</span>
                          </div>
                          <div className="mt-3 space-y-2">
                            {selectedDocuments.length ? (
                              selectedDocuments.map((doc) => (
                                <a
                                  key={doc.id}
                                  href={doc.file_url || "#"}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 transition hover:border-blue-100 hover:bg-gray-50"
                                >
                                  <div className="min-w-0">
                                    <div className="truncate font-medium text-gray-900">{doc.file_name}</div>
                                    <div className="text-xs text-gray-500">
                                      {doc.doc_type} | {doc.uploaded_by_name || "—"} | {formatDateTime(doc.uploaded_at)}
                                    </div>
                                  </div>
                                  <i className="fa-solid fa-arrow-up-right-from-square text-gray-400" />
                                </a>
                              ))
                            ) : (
                              <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-5 text-sm text-gray-500">
                                No liquidation documents uploaded yet.
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-5">
                          <h4 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">
                            Timeline
                          </h4>
                          <div className="mt-3 space-y-2">
                            {timeline.length ? (
                              timeline.map((item) => (
                                <div key={`${item.label}-${item.value}-${item.actor}`} className="flex gap-3 rounded-2xl border border-gray-100 bg-white p-3">
                                  <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                                    <i className="fa-solid fa-clock text-sm" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900">{item.label}</div>
                                    <div className="text-sm text-gray-600">
                                      {item.actor ? `${item.actor} • ` : ""}
                                      {formatDateTime(item.value)}
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-5 text-sm text-gray-500">
                                No liquidation activity yet.
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-5">
                          <h4 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">
                            Particulars
                          </h4>
                          <div className="mt-3 space-y-2">
                            {(modalData?.particulars || []).length ? (
                              modalData.particulars.map((particular) => (
                                <div
                                  key={particular.id || particular.description}
                                  className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3"
                                >
                                  <div className="font-semibold text-gray-900">
                                    {particular.description || "—"}
                                  </div>
                                  <div className="mt-1 text-sm text-gray-600">
                                    Qty {Number(particular.quantity || 0)} | Unit ₱{money(particular.amount || 0)} | Total ₱{money(particular.total || 0)}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-5 text-sm text-gray-500">
                                No particulars available.
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  </div>

                  <div className="bg-gray-50/80">
                    <div className="p-6">
                    <h4 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">
                      Actions
                    </h4>

                    <div className="mt-4 space-y-4">
                      <div className="rounded-2xl border border-gray-100 bg-white p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold text-gray-900">
                                {canUpload ? "Submit documents" : "Submitted documents"}
                              </div>
                              <div className="mt-1 text-sm text-gray-600">
                                {canUpload
                                ? "Add supporting documents, then submit them together with the liquidation report."
                                : "These are the files already submitted for the liquidation."}
                              </div>
                            </div>
                          <span className="text-xs text-gray-500">
                            {canUpload ? "Open" : "Locked"}
                          </span>
                        </div>
                        {selectedDocuments.length ? (
                          <div className="mt-4 space-y-2">
                            {selectedDocuments.map((doc) => (
                              <a
                                key={doc.id || doc.file_path}
                                href={doc.file_url || "#"}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 transition hover:border-blue-100 hover:bg-gray-50"
                              >
                                <div className="min-w-0">
                                  <div className="truncate font-medium text-gray-900">{doc.file_name || "—"}</div>
                                  <div className="text-xs text-gray-500">
                                    {doc.doc_type || "document"} | {doc.uploaded_by_name || "—"} | {formatDateTime(doc.uploaded_at)}
                                  </div>
                                </div>
                                <i className="fa-solid fa-arrow-up-right-from-square text-gray-400" />
                              </a>
                            ))}
                          </div>
                        ) : null}

                        {canUpload ? (
                          <div className="mt-4 space-y-3">
                            <div className="space-y-3">
                              {uploadDocuments.map((entry, index) => (
                                <div key={entry.id} className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                                      Document {index + 1}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeUploadDocumentRow(entry.id)}
                                      disabled={uploadDocuments.length === 1}
                                      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                      Remove
                                    </button>
                                  </div>
                                  <div className="mt-3 space-y-3">
                                    <div>
                                      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                                        Document type
                                      </label>
                                      <select
                                        value={entry.docType}
                                        onChange={(e) => updateUploadDocumentRow(entry.id, "docType", e.target.value)}
                                        className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                                      >
                                        <option value="receipt">Receipt</option>
                                        <option value="proof_of_payment">Proof of payment</option>
                                        <option value="other">Other</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                                        File
                                      </label>
                                      <input
                                        type="file"
                                        accept=".pdf,.png,.jpg,.jpeg,image/*,application/pdf"
                                        onChange={(e) =>
                                          updateUploadDocumentRow(entry.id, "file", e.target.files?.[0] || null)
                                        }
                                        className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                                      />
                                      <div className="mt-2 text-xs text-gray-500">
                                        {entry.file ? entry.file.name : "No file selected"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={addUploadDocumentRow}
                              className="inline-flex items-center gap-2 rounded-2xl border border-dashed border-blue-300 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                            >
                              <i className="fa-solid fa-plus" />
                              Add new document
                            </button>
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                                Notes
                              </label>
                              <textarea
                                value={uploadNote}
                                onChange={(e) => setUploadNote(e.target.value)}
                                rows={3}
                                placeholder="Optional submission note"
                                className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                                Team leader
                              </label>
                              <select
                                value={selectedTeamLeaderId}
                                onChange={(e) => setSelectedTeamLeaderId(e.target.value)}
                                className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                              >
                                <option value="">Select team leader</option>
                                {teamLeaderUsers.map((u) => (
                                  <option key={u.user_id} value={u.user_id}>
                                    {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.user_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                              The documents will be submitted together with the liquidation report using the button at the bottom.
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {showSubmissionActions && (
                        <div className="rounded-2xl border border-gray-100 bg-white p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold text-gray-900">Approve liquidation</div>
                              <div className="mt-1 text-sm text-gray-600">
                                Finalize the liquidation once the hardcopy has been checked.
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleTransition("verify")}
                            disabled={!canVerify || isVerifyLoading}
                            title={verifyDisabledReason || "Approve liquidation"}
                            className="mt-4 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {isVerifyLoading ? (
                              <span className="inline-flex items-center justify-center gap-2">
                                <i className="fa-solid fa-spinner fa-spin" />
                                Approving...
                              </span>
                            ) : (
                              "Approve as Team Leader"
                            )}
                          </button>
                          {verifyDisabledReason ? (
                            <p className="mt-2 text-xs font-medium text-amber-700">
                              {verifyDisabledReason}
                            </p>
                          ) : null}
                        </div>
                      )}

                      {showAccountingReviewActions ? (
                        <div className="rounded-2xl border border-gray-100 bg-white p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold text-gray-900">Reject liquidation</div>
                              <div className="mt-1 text-sm text-gray-600">
                                Send the liquidation back if the submitted package has issues.
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 space-y-3">
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                                Reject reason
                              </label>
                              <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={3}
                                placeholder="State why the liquidation is rejected"
                                className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleTransition("reject", { reason: rejectReason })}
                              disabled={!canReject || !rejectReason.trim() || isRejectLoading}
                              className="w-full rounded-2xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isRejectLoading ? (
                                <span className="inline-flex items-center justify-center gap-2">
                                  <i className="fa-solid fa-spinner fa-spin" />
                                  Rejecting...
                                </span>
                              ) : (
                                "Reject Liquidation"
                              )}
                            </button>
                          </div>
                        </div>
                      ) : null}

                    </div>
                    </div>
                  </div>
                </div>
                {!hideReportEditor && (
                  <div
                    ref={reportSectionRef}
                    id="liquidation-report-section"
                    className="col-span-1 lg:col-span-2 border-t border-gray-100 bg-white px-6 py-6"
                  >
                  <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-gray-900">Liquidation report</div>
                        <div className="mt-1 text-sm text-gray-600">
                          The assigned team leader can prepare the liquidation report while awaiting documents, then approve the liquidation after upload.
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {canCreateReport ? "Ready" : "Locked"}
                      </span>
                    </div>
                    {!canCreateReport ? (
                      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        The liquidation report can be prepared while awaiting documents and must be saved before team leader approval.
                      </div>
                    ) : (
                      savedReport?.report_format === "manual" ? (
                        <div className="mt-4 space-y-5">
                          <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-800">
                            <span className="font-semibold">Manual liquidation</span>
                            <span className="ml-2">This report is connected to the selected RFP and follows the normal approval flow.</span>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2">
                            {[
                              ["Reference", savedReport.reference_no],
                              ["Name", savedReport.manual_name || savedReport.consignee],
                              ["Department", savedReport.manual_department],
                              ["Liquidation Date", savedReport.report_date_created],
                              ["Requested Date", savedReport.delivery_date],
                              ["Job Order No.", savedReport.job_order],
                              ["Vehicle Plate No.", savedReport.manual_vehicle_plate || savedReport.dock],
                              ["Remarks", savedReport.manual_remarks || savedReport.shipment_details],
                            ].map(([label, value]) => (
                              <div key={label}>
                                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">{label}</div>
                                <div className="mt-2 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
                                  {value || "—"}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                            <div className="font-semibold text-gray-900">Expenses</div>
                            <div className="mt-3 divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white">
                              {(savedReport.actual_cost_rows || []).map((row, index) => (
                                <div key={`${row.id || row.particulars}-${index}`} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                  <span className="text-gray-700">{row.particulars || "Other"}</span>
                                  <span className="font-semibold text-gray-900">₱{money(row.amount || 0)}</span>
                                </div>
                              ))}
                              {(savedReport.advance_rows || []).map((row, index) => (
                                <div key={`advance-${row.id || index}`} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                  <span className="text-gray-700">{row.particulars || "Cash Advance"}</span>
                                  <span className="font-semibold text-gray-900">₱{money(row.amount || 0)}</span>
                                </div>
                              ))}
                              {!savedReport.actual_cost_rows?.length && !savedReport.advance_rows?.length ? (
                                <div className="px-4 py-3 text-sm text-gray-500">No expense lines recorded.</div>
                              ) : null}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => submitLiquidationReport({ includeDocuments: hasUploadFiles })}
                            disabled={!canSubmitReportAction || isUploadLoading || reportLoading || !selectedTeamLeaderUserId}
                            className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {isUploadLoading || reportLoading ? "Submitting..." : reportActionLabel}
                          </button>
                        </div>
                      ) : (
                      <div className="mt-4 space-y-5">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Date created
                            </label>
                            <input
                              value={reportDateCreated}
                              onChange={(e) => setReportDateCreated(e.target.value)}
                              type="date"
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Consignee
                            </label>
                            <input
                              value={shipmentDetails?.receiver_name || savedReport?.consignee || ""}
                              readOnly
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm text-gray-700"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              JO
                            </label>
                            <input
                              value={modalData?.job_order || ""}
                              readOnly
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm text-gray-700"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Tracking number
                            </label>
                            <input
                              value={shipmentDetails?.tracking_no || savedReport?.tracking_no || ""}
                              readOnly
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm text-gray-700"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Shipment details
                            </label>
                            <textarea
                              value={shipmentDetails?.shipment_description || savedReport?.shipment_details || ""}
                              readOnly
                              rows={2}
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm text-gray-700"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Dock
                            </label>
                            <input
                              value={reportDock}
                              onChange={(e) => setReportDock(e.target.value)}
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              P.O No
                            </label>
                            <input
                              value={reportPoNo}
                              onChange={(e) => setReportPoNo(e.target.value)}
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Reference number
                            </label>
                            <input
                              value={reportReferenceNo}
                              onChange={(e) => setReportReferenceNo(e.target.value)}
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Delivery date
                            </label>
                            <input
                              value={reportDeliveryDate}
                              onChange={(e) => setReportDeliveryDate(e.target.value)}
                              type="date"
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Team leader
                            </label>
                            <input
                              value={
                                assignedTeamLeader
                                  ? [assignedTeamLeader.first_name, assignedTeamLeader.last_name].filter(Boolean).join(" ") || assignedTeamLeader.user_name
                                  : ""
                              }
                              readOnly
                              placeholder="Select a team leader when uploading documents"
                              className="mt-2 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm text-gray-700"
                            />
                          </div>
                          <div className="sm:col-span-2 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <div className="font-semibold text-gray-900">Team leader signature</div>
                                <div className="text-sm text-gray-600">
                                  Visible after the team leader approves the liquidation.
                                </div>
                              </div>
                              <span
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                  savedReport?.approver_signature_url || savedReport?.approved_at
                                    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                    : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                                }`}
                              >
                                {approverBadge}
                              </span>
                            </div>
                            <div className="mt-4 rounded-2xl border border-dashed border-gray-200 bg-white p-4">
                              <div className="flex min-h-[88px] items-center justify-center">
                                {savedReport?.team_leader_approved_signature_url ? (
                                  <img
                                    src={savedReport.team_leader_approved_signature_url}
                                    alt="Team Leader Signature"
                                    className="max-h-20 max-w-full object-contain"
                                  />
                                ) : (
                                  <div className="text-center text-sm text-gray-500">
                                    No team leader signature available yet.
                                  </div>
                                )}
                              </div>
                              <div className="mt-3 text-center text-sm font-semibold text-gray-900">
                                {teamLeaderDisplayName}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="font-semibold text-gray-900">Cost incurred for shipment</div>
                              <div className="text-sm text-gray-600">Manual input.</div>
                            </div>
                            <button
                              type="button"
                              onClick={addActualCostRow}
                              className="rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue transition hover:bg-blue-50"
                            >
                              Add row
                            </button>
                          </div>
                          <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4">
                            <div className="mb-3 flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-gray-900">Request Particular Sources</div>
                                <div className="text-xs text-gray-500">
                                  Choose which request particulars to add as cost incurred.
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => addAllRequestParticularsToActualCosts(modalData?.particulars)}
                                disabled={!modalData?.particulars?.length}
                                className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Add All
                              </button>
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2">
                              {modalData?.particulars?.length ? (
                                modalData.particulars.map((particular) => {
                                  const added = isRequestParticularAdded(particular);
                                  return (
                                    <div
                                      key={particular.id || getRequestParticularLabel(particular)}
                                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2"
                                    >
                                      <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-gray-800">
                                          {getRequestParticularLabel(particular) || "—"}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {money(getRequestParticularAmount(particular))}
                                        </div>
                                      </div>
                                      {added ? (
                                        <button
                                          type="button"
                                          onClick={() => removeRequestParticularFromActualCosts(particular)}
                                          className="shrink-0 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                                        >
                                          Remove
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => addRequestParticularToActualCosts(particular)}
                                          className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                                        >
                                          Add
                                        </button>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-500 sm:col-span-2">
                                  No request particulars available.
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full border-collapse">
                              <thead>
                                <tr className="text-left text-xs uppercase tracking-[0.12em] text-gray-500">
                                  <th className="border border-gray-200 bg-white px-3 py-2">Particulars</th>
                                  <th className="border border-gray-200 bg-white px-3 py-2">OR No.</th>
                                  <th className="border border-gray-200 bg-white px-3 py-2">Unliquidated</th>
                                  <th className="border border-gray-200 bg-white px-3 py-2 text-right">Unliquidated Amount</th>
                                  <th className="border border-gray-200 bg-white px-3 py-2 text-right">Amount</th>
                                  <th className="border border-gray-200 bg-white px-3 py-2 text-right">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {actualCostRows.map((row, index) => (
                                  <tr key={`actual-${index}`}>
                                    <td className="border border-gray-200 bg-white p-2">
                                      <select
                                        value={row.particulars}
                                        onChange={(e) => updateActualCostRow(index, "particulars", e.target.value)}
                                        className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                                      >
                                        <option value="">Select particulars</option>
                                        {row.particulars &&
                                        !liquidationParticularsOptions.some(
                                          (option) => option.name === row.particulars
                                        ) ? (
                                          <option value={row.particulars}>{row.particulars}</option>
                                        ) : null}
                                        {liquidationParticularsOptions.map((option) => (
                                          <option key={option.id} value={option.name}>
                                            {option.name}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                    <td className="border border-gray-200 bg-white p-2">
                                      <input
                                        value={row.or_no}
                                        onChange={(e) => updateActualCostRow(index, "or_no", e.target.value)}
                                        className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                                      />
                                    </td>
                                    <td className="border border-gray-200 bg-white p-2">
                                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                        <input
                                          type="checkbox"
                                          checked={Boolean(row.is_unliquidated)}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            updateActualCostRow(index, "is_unliquidated", checked);
                                            if (!checked) {
                                              updateActualCostRow(index, "amount", "");
                                            }
                                          }}
                                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span>Unliquidated</span>
                                      </label>
                                    </td>
                                    <td className="border border-gray-200 bg-white p-2">
                                      <input
                                        type="text"
                                        value={row.is_unliquidated ? money(row.amount) : ""}
                                        readOnly
                                        placeholder={row.is_unliquidated ? "0.00" : ""}
                                        className="w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-right text-gray-700"
                                      />
                                    </td>
                                    <td className="border border-gray-200 bg-white p-2">
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={row.amount}
                                        onChange={(e) => updateActualCostRow(index, "amount", e.target.value)}
                                        className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-right"
                                      />
                                    </td>
                                    <td className="border border-gray-200 bg-white p-2 text-right">
                                      <button
                                        type="button"
                                        onClick={() => removeActualCostRow(index)}
                                        disabled={actualCostRows.length === 1}
                                        className="rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-3 flex justify-end">
                            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900">
                              Total Actual Cost Incurred: ₱{money(actualCostTotal)}
                            </div>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                          <div>
                            <div className="font-semibold text-gray-900">Advances table</div>
                            <div className="text-sm text-gray-600">
                              Particulars are fetched from the request.
                            </div>
                          </div>
                          <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full border-collapse">
                              <thead>
                                <tr className="text-left text-xs uppercase tracking-[0.12em] text-gray-500">
                                  <th className="border border-gray-200 bg-white px-3 py-2">Particulars</th>
                                  <th className="border border-gray-200 bg-white px-3 py-2">OR No.</th>
                                  <th className="border border-gray-200 bg-white px-3 py-2 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {advanceRows.length ? (
                                  advanceRows.map((row) => (
                                    <tr key={row.id}>
                                      <td className="border border-gray-200 bg-white px-3 py-2 text-sm">{row.particulars || "—"}</td>
                                      <td className="border border-gray-200 bg-white p-2">
                                        <input
                                          value={row.or_no}
                                          onChange={(e) => updateAdvanceRow(row.id, "or_no", e.target.value)}
                                          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                                        />
                                      </td>
                                      <td className="border border-gray-200 bg-white p-2">
                                        <input
                                          value={money(row.amount)}
                                          readOnly
                                          className="w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-right text-gray-700"
                                        />
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={6} className="border border-gray-200 bg-white px-3 py-4 text-sm text-gray-500">
                                      No request particulars found.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-3 flex justify-end gap-3">
                            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900">
                              Total Advances: ₱{money(advancesTotal)}
                            </div>
                            <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue">
                              {reimbursementTotal < 0
                                ? "Return Subtotal"
                                : reimbursementTotal > 0
                                  ? "Reimbursement Subtotal"
                                  : "Liquidation"}: ₱{money(getLiquidationSettlementAmount(reimbursementTotal))}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => submitLiquidationReport({ includeDocuments: hasUploadFiles })}
                          disabled={
                            !canSubmitReportAction ||
                            isUploadLoading ||
                            reportLoading ||
                            !selectedTeamLeaderUserId
                          }
                          className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isUploadLoading || reportLoading ? (
                            <span className="inline-flex items-center justify-center gap-2">
                              <i className="fa-solid fa-spinner fa-spin" />
                              Submitting...
                            </span>
                          ) : (
                            reportActionLabel
                          )}
                        </button>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {successModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
              onClick={() => setSuccessModal(null)}
              role="presentation"
            />
            <div className="relative z-10 w-full max-w-md rounded-[1.75rem] bg-white shadow-2xl ring-1 ring-black/5">
              <div className="flex items-start gap-4 p-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                  <i className="fa-solid fa-circle-check text-xl" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Success</p>
                  <h3 className="mt-1 text-2xl font-semibold text-gray-900">
                    {successModal.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">{successModal.message}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSuccessModal(null)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                  aria-label="Close success modal"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
              <div className="border-t border-gray-100 p-4">
                <button
                  type="button"
                  onClick={() => setSuccessModal(null)}
                  className="w-full rounded-2xl bg-blue px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue/90"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {!hideReportEditor && reportLoading &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[1095] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm" role="presentation" />
            <div className="relative z-10 w-full max-w-md rounded-[1.75rem] bg-white shadow-2xl ring-1 ring-black/5">
              <div className="flex items-start gap-4 p-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue">
                  <i className="fa-solid fa-spinner fa-spin text-xl" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Saving</p>
                  <h3 className="mt-1 text-2xl font-semibold text-gray-900">
                    Creating liquidation report
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Please wait while the report is being submitted and prepared.
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-100 p-4">
                <div className="w-full rounded-2xl bg-gray-50 px-4 py-3 text-sm text-gray-600">
                  Saving report and updating liquidation details...
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default LiquidationWorkflow;
